import { useCallback, useMemo } from "react";
import { useFeatureFlags } from "@apac/feature-flags";
import { useI18nContext } from "@hopper-b2b/i18n";
import { merge } from "lodash-es";

export type CallToAction = {
  label: string;
  url: string | "close";
  variant?: "outlined" | "primary" | "secondary";
};

/**
 * This is the base interface for the dynamic offer content we expect from launch darkly.
 * We cannot insist on this but we can use this as a guide for the content we expect.
 * We should try keep the interface as close as possible to the one we expect from launch darkly,
 * But tenants are likely to have slight differences. Based on their offer UX.
 */
export interface DynamicOfferContent {
  key: string;
  name?: string;
  priority?: number;
  title: string;
  subtitle?: string;
  tag?: string;
  image: string;
  hotelOffer?: {
    terms: string;
    lodgingIds: string[];
    defaultSearch?: {
      startDate?: string;
      endDate?: string;
      guestCounts: {
        adults: number;
        children: number[];
      };
      rooms: number;
      lodgingSelection?: {
        LodgingSelection: "Location" | "LodgingIds" | "Market" | "Place";
        // Place
        placeId?: string;
        searchTerm?: string;
        placeTypes?: string[];
        // LodgingIds
        lodgingIds?: string[];
        preserveOrder?: boolean;
        // Market
        groupingKey?: {
          name: string;
          fullName: string;
        };
        // Location
        descriptor?: Record<string, unknown>;
        center?: { lat: number; lon: number };
      };
    };
  };
  flightOffer?: {
    terms: string;
    defaultSearch?: {
      destination: {
        code: string;
        label: string;
        regionType?: string;
      };
      departureDate?: string;
      returnDate?: string;
      tripCategory?: string;
      passengers: {
        adultsCount: number;
        childrenCount: number;
        infantsCount: number;
      };
    };
  };
  saleEndDate?: string;
  coupons?: Array<{
    icon: string;
    label: string;
    heading: string;
    terms: string;
  }>;
  highlightsHeading?: string;
  highlights?: Array<{
    title: string;
    description: string;
    image: string;
  }>;
  footerCTA?: {
    heading: string;
    hotelButtonLabel?: string;
    flightButtonLabel?: string;
  };
  callToAction?: Array<CallToAction>;
  // Description populates a modal, read more URL navigates to a page.
  readMoreUrl?: string;
  description?: {
    title: string;
    body?: string;
    callToAction?: Array<CallToAction>;
  };

  /**
   * Optional locale-specific content overrides.
   * When present, content for the current locale will be merged with the base offer.
   * Falls back to base content if the current locale is not found.
   */
  localized?: Record<string, Partial<Omit<DynamicOfferContent, "localized">>>;
}

interface UseDynamicOffersParams {
  flagPrefix: string;
  filterByTitle?: boolean;
}

interface UseDynamicOffersReturn<T> {
  availableOffers: Record<string, T>;
  sortedOffers: T[];
  getOfferByName: (name: string) => T | undefined;
}

const localizeOffer =
  (language: string) =>
  <T extends { localized?: Record<string, any> }>(offer: T): T => {
    const localized = offer.localized?.[language];
    return merge(offer, localized);
  };

export const useDynamicOffers = <
  T extends Pick<
    DynamicOfferContent,
    "name" | "key" | "priority" | "localized" | "title"
  >,
>({
  flagPrefix,
  filterByTitle = true,
}: UseDynamicOffersParams): UseDynamicOffersReturn<T> => {
  const { featureFlags } = useFeatureFlags();
  const { language } = useI18nContext();

  const offers = useMemo(
    () =>
      featureFlags
        .filter(
          ({ featureFlagName, variant, jsonPayload }) =>
            featureFlagName.startsWith(flagPrefix) &&
            variant.toLocaleLowerCase() === "available" &&
            jsonPayload
        )
        .map(
          (flag) =>
            ({
              ...flag.jsonPayload,
              key: flag.featureFlagName,
              name: flag.featureFlagName,
            }) as T
        )
        .map(localizeOffer(language))
        .filter((offer) => !filterByTitle || Boolean(offer.title))
        .sort((a, b) => (a.priority || 0) - (b.priority || 0)),
    [featureFlags, flagPrefix, language, filterByTitle]
  );

  const availableOffers = useMemo(
    () =>
      offers.reduce(
        (acc, offer) => ({
          ...acc,
          [offer.key]: offer,
        }),
        {} as Record<string, T>
      ),
    [offers]
  );

  return {
    availableOffers,
    sortedOffers: offers,
    getOfferByName: useCallback(
      (name: string) => offers.find((offer) => offer.name === name),
      [offers]
    ),
  };
};
